import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Chip,
  Button,
  Avatar,
} from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import VideoCallRoundedIcon from "@mui/icons-material/VideoCallRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import MedicalInformationOutlinedIcon from "@mui/icons-material/MedicalInformationOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";

const Scheduler = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const [currentDate] = useState("Friday, September 11, 2026");
  const [viewMode, setViewMode] = useState("Day");
  const [viewAllStaff, setViewAllStaff] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState([]);

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F"; // Form outline border & Top button
  const tealButton = "#245E65"; // Action buttons
  const addGreen = "#057A44"; // Book Slot button
  const labelTeal = "#276A6C"; // Input label teal color
  const pastelTealBg = "#D9EBEF"; // Soft clinical badge background

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const gridLineColor = isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#CBD5E1";

  const staffOptions = [
    "TruEvv (owais@gmail.com)",
    "Dr. Usman (Lead Neurologist)",
    "Dr. Sarah Jenkins (Neuropsychiatrist)",
    "Clinical Care Team A",
  ];

  const handleStaffDelete = (staffToDelete) => {
    setSelectedStaff((prev) => prev.filter((s) => s !== staffToDelete));
  };

  const timeSlots = [
    "all-day",
    "8:00am", "8:30am",
    "9:00am", "9:30am",
    "10:00am", "10:30am",
    "11:00am", "11:30am",
    "12:00pm", "12:30pm",
    "1:00pm", "1:30pm",
    "2:00pm", "2:30pm",
    "3:00pm", "3:30pm",
    "4:00pm", "4:30pm",
    "5:00pm", "5:30pm",
    "6:00pm",
  ];

  const scheduledAppointments = [
    {
      id: "apt-1",
      patientName: "Alexander Wright",
      service: "Migraine Protocol Review",
      time: "9:00 AM - 10:00 AM",
      startSlot: "9:00am",
      durationSlots: 2,
      type: "In-Clinic",
      status: "Completed",
      room: "Suite 4B",
    },
    {
      id: "apt-2",
      patientName: "Charlotte Bennett",
      service: "Routine Neuro Diagnostics",
      time: "11:00 AM - 11:30 AM",
      startSlot: "11:00am",
      durationSlots: 1,
      type: "Telehealth",
      status: "Completed",
      room: "Virtual Room 2",
    },
    {
      id: "apt-3",
      patientName: "Lucas Miller",
      service: "Neuropathy Treatment Followup",
      time: "1:30 PM - 2:30 PM",
      startSlot: "1:30pm",
      durationSlots: 2,
      type: "In-Clinic",
      status: "In-Session",
      room: "Neuro Lab 1",
    },
    {
      id: "apt-4",
      patientName: "Sophia Anderson",
      service: "Post-Concussion Comprehensive Check",
      time: "3:00 PM - 3:30 PM",
      startSlot: "3:00pm",
      durationSlots: 1,
      type: "Telehealth",
      status: "Confirmed",
      room: "Virtual Room 1",
    },
    {
      id: "apt-5",
      patientName: "Oliver Davies",
      service: "Epilepsy EEG Consultation",
      time: "5:00 PM - 5:30 PM",
      startSlot: "5:00pm",
      durationSlots: 1,
      type: "In-Clinic",
      status: "Scheduled",
      room: "Suite 4A",
    },
  ];

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. CLINICAL PULSE PILLS */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            p: 2,
            borderRadius: "14px",
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 1.8,
            boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 10px rgba(15, 23, 42, 0.02)",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: pastelTealBg,
              color: tealButton,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EventAvailableRoundedIcon sx={{ fontSize: "1.3rem" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
              Total Slots Today
            </Typography>
            <Typography sx={{ fontSize: "1.3rem", fontWeight: 800, color: primaryTextColor }}>
              {scheduledAppointments.length} Booked
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            borderRadius: "14px",
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 1.8,
            boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 10px rgba(15, 23, 42, 0.02)",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: pastelTealBg,
              color: tealButton,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <VideoCallRoundedIcon sx={{ fontSize: "1.3rem" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
              Telehealth Encounters
            </Typography>
            <Typography sx={{ fontSize: "1.3rem", fontWeight: 800, color: primaryTextColor }}>
              {scheduledAppointments.filter((a) => a.type === "Telehealth").length} Calls
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            borderRadius: "14px",
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 1.8,
            boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 10px rgba(15, 23, 42, 0.02)",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: pastelTealBg,
              color: tealButton,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonRoundedIcon sx={{ fontSize: "1.3rem" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
              In-Clinic Encounters
            </Typography>
            <Typography sx={{ fontSize: "1.3rem", fontWeight: 800, color: primaryTextColor }}>
              {scheduledAppointments.filter((a) => a.type === "In-Clinic").length} Visits
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            borderRadius: "14px",
            backgroundColor: cardBg,
            border: `1px solid ${cardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 1.8,
            boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 2px 10px rgba(15, 23, 42, 0.02)",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: pastelTealBg,
              color: tealButton,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AccessTimeRoundedIcon sx={{ fontSize: "1.3rem" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
              Active Room Slots
            </Typography>
            <Typography sx={{ fontSize: "1.3rem", fontWeight: 800, color: primaryTextColor }}>
              3 Suites Open
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* 2. SCHEDULER CONTROLLER BAR (OVERFLOW FIX) */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${tealMain}`,
          p: { xs: 2, md: 2.5 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 2, md: 2.5 },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.03)",
          mb: 3,
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {/* Left: Date Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.8, flexShrink: 0 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "12px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CalendarMonthRoundedIcon sx={{ fontSize: "1.5rem" }} />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: { xs: "0.95rem", md: "1.05rem" },
                fontWeight: 800,
                color: primaryTextColor,
                letterSpacing: "-0.3px",
                lineHeight: 1.2,
                mb: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              {currentDate}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Tooltip title="Previous Day">
                <IconButton
                  size="small"
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    border: `1px solid ${controlBorder}`,
                    color: secondaryTextColor,
                    "&:hover": { color: tealMain, borderColor: tealMain },
                  }}
                >
                  <ChevronLeftRoundedIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Jump to Today">
                <IconButton
                  size="small"
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    border: `1px solid ${controlBorder}`,
                    color: secondaryTextColor,
                    "&:hover": { color: tealMain, borderColor: tealMain },
                  }}
                >
                  <TodayRoundedIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Next Day">
                <IconButton
                  size="small"
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "6px",
                    backgroundColor: controlBg,
                    border: `1px solid ${controlBorder}`,
                    color: secondaryTextColor,
                    "&:hover": { color: tealMain, borderColor: tealMain },
                  }}
                >
                  <ChevronRightRoundedIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Center: Flexible Provider Select */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.4,
            flex: 1,
            minWidth: 0,
            width: { xs: "100%", md: "auto" },
            maxWidth: { md: "460px" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: labelTeal }}>
              Assigned Staff & Providers
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={viewAllStaff}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setViewAllStaff(checked);
                    setSelectedStaff(checked ? [...staffOptions] : []);
                  }}
                  size="small"
                  sx={{ p: 0.2, color: secondaryTextColor, "&.Mui-checked": { color: tealMain } }}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: secondaryTextColor, cursor: "pointer", whiteSpace: "nowrap" }}>
                  View All Providers
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Box>

          <Select
            multiple
            displayEmpty
            value={selectedStaff}
            onChange={(e) =>
              setSelectedStaff(
                typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value
              )
            }
            IconComponent={KeyboardArrowDownRoundedIcon}
            renderValue={(selected) => {
              if (!selected || selected.length === 0) {
                return (
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      color: secondaryTextColor,
                      fontWeight: 500,
                      userSelect: "none",
                    }}
                  >
                    Select Assigned Provider...
                  </Typography>
                );
              }

              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, maxHeight: "56px", overflowY: "auto" }}>
                  {selected.map((val) => (
                    <Chip
                      key={val}
                      label={val}
                      size="small"
                      onDelete={(e) => {
                        e.stopPropagation();
                        handleStaffDelete(val);
                      }}
                      sx={{
                        height: "22px",
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        backgroundColor: pastelTealBg,
                        color: tealButton,
                        border: "1px solid #B8D6DD",
                        "& .MuiChip-deleteIcon": {
                          color: tealButton,
                          fontSize: "0.85rem",
                          "&:hover": { color: "#EF4444" },
                        },
                      }}
                    />
                  ))}
                </Box>
              );
            }}
            sx={{
              minHeight: "40px",
              borderRadius: "8px",
              backgroundColor: controlBg,
              fontSize: "0.82rem",
              color: primaryTextColor,
              "& fieldset": { borderColor: controlBorder },
              "&:hover fieldset": { borderColor: tealMain },
              "&.Mui-focused fieldset": { borderColor: tealMain },
              "& .MuiSelect-select": { py: "6px", display: "flex", alignItems: "center" },
            }}
          >
            {staffOptions.map((name) => (
              <MenuItem key={name} value={name} sx={{ fontSize: "0.82rem" }}>
                <Checkbox size="small" checked={selectedStaff.indexOf(name) > -1} sx={{ mr: 1, p: 0.2, color: tealMain, "&.Mui-checked": { color: tealMain } }} />
                {name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Right: View Mode & Book Slot Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            flexShrink: 0,
            width: { xs: "100%", md: "auto" },
            justifyContent: { xs: "flex-end", md: "flex-start" },
          }}
        >
          <Select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            IconComponent={KeyboardArrowDownRoundedIcon}
            sx={{
              height: "40px",
              minWidth: "90px",
              borderRadius: "8px",
              backgroundColor: controlBg,
              color: primaryTextColor,
              fontWeight: 700,
              fontSize: "0.82rem",
              "& fieldset": { borderColor: controlBorder },
              "&:hover fieldset": { borderColor: tealMain },
              "&.Mui-focused fieldset": { borderColor: tealMain },
              "& .MuiSelect-icon": { color: secondaryTextColor },
            }}
          >
            <MenuItem value="Day" sx={{ fontSize: "0.82rem", fontWeight: 600 }}>Day</MenuItem>
            <MenuItem value="Week" sx={{ fontSize: "0.82rem", fontWeight: 600 }}>Week</MenuItem>
            <MenuItem value="Month" sx={{ fontSize: "0.82rem", fontWeight: 600 }}>Month</MenuItem>
            <MenuItem value="Agenda" sx={{ fontSize: "0.82rem", fontWeight: 600 }}>Agenda</MenuItem>
          </Select>

          <Button
            variant="contained"
            startIcon={<AddRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              backgroundColor: addGreen,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.8rem",
              borderRadius: "8px",
              px: 2,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { backgroundColor: "#046337" },
            }}
          >
            Book Slot
          </Button>
        </Box>
      </Box>

      {/* 3. DAY-VIEW TIMELINE GRID */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "14px",
          border: `1px solid ${cardBorder}`,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.03)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            py: 1.6,
            px: 3,
            backgroundColor: isDark ? "#162238" : "#F8FAFC",
            borderBottom: `1px solid ${cardBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 800,
                color: tealButton,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Friday Schedule
            </Typography>
            <Chip
              label="Active Operations"
              size="small"
              sx={{
                height: "20px",
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: pastelTealBg,
                color: tealButton,
                borderRadius: "6px",
              }}
            />
          </Box>

          <Typography sx={{ fontSize: "0.78rem", color: secondaryTextColor, fontWeight: 600 }}>
            Timezone: PKT (UTC+5)
          </Typography>
        </Box>

        <Box
          sx={{
            maxHeight: "580px",
            overflowY: "auto",
            overflowX: "auto",
            position: "relative",
            "&::-webkit-scrollbar": { width: "6px", height: "6px" },
            "&::-webkit-scrollbar-track": { backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#F8FAFC" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.15)" : "#CBD5E1",
              borderRadius: "10px",
              "&:hover": { backgroundColor: tealMain },
            },
          }}
        >
          <Box sx={{ minWidth: "900px", position: "relative" }}>
            {/* Real-time Indicator Line */}
            <Box
              sx={{
                position: "absolute",
                top: "848px",
                left: 0,
                right: 0,
                height: "2px",
                backgroundColor: tealMain,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor: tealMain,
                  position: "absolute",
                  left: "75px",
                  boxShadow: `0 0 10px ${tealMain}`,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: "95px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  backgroundColor: pastelTealBg,
                  border: `1px solid ${tealMain}`,
                  px: 1,
                  py: 0.2,
                  borderRadius: "6px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: tealMain }} />
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: tealButton }}>
                  4:56 PM • CURRENT TIME
                </Typography>
              </Box>
            </Box>

            {/* Time Slot Grid */}
            {timeSlots.map((time, index) => {
              const apt = scheduledAppointments.find((a) => a.startSlot === time);

              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    minHeight: time === "all-day" ? "44px" : "48px",
                    borderBottom: `1px solid ${time.includes(":30") ? "rgba(226, 232, 240, 0.4)" : gridLineColor}`,
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "#F8FAFC",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "85px",
                      minWidth: "85px",
                      pr: 1.8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      borderRight: `1px solid ${cardBorder}`,
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.01)" : "#FDFDFD",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        color: time === "all-day" ? tealButton : secondaryTextColor,
                        textTransform: time === "all-day" ? "uppercase" : "none",
                      }}
                    >
                      {time}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      px: 2,
                      py: 0.6,
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {apt && (
                      <Box
                        sx={{
                          width: "96%",
                          height: apt.durationSlots === 2 ? "88px" : "40px",
                          position: "absolute",
                          top: "4px",
                          left: "14px",
                          zIndex: 5,
                          borderRadius: "10px",
                          backgroundColor: isDark ? "rgba(30, 94, 95, 0.16)" : pastelTealBg,
                          border: `1px solid ${tealMain}`,
                          borderLeft: `5px solid ${tealMain}`,
                          px: 1.8,
                          py: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          transition: "all 0.22s ease",
                          "&:hover": {
                            transform: "translateY(-1px) translateX(4px)",
                            boxShadow: "0 6px 18px rgba(30, 94, 95, 0.15)",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: "0.75rem",
                              fontWeight: 800,
                              backgroundColor: tealButton,
                              color: "#FFFFFF",
                            }}
                          >
                            {apt.patientName[0]}
                          </Avatar>
                          <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: primaryTextColor }}>
                                {apt.patientName}
                              </Typography>
                              <Chip
                                label={apt.room}
                                size="small"
                                sx={{
                                  height: "18px",
                                  fontSize: "0.65rem",
                                  fontWeight: 700,
                                  backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "#FFFFFF",
                                  color: tealButton,
                                  border: "1px solid rgba(36, 94, 101, 0.2)",
                                  borderRadius: "4px",
                                }}
                              />
                            </Box>
                            <Typography sx={{ fontSize: "0.72rem", color: secondaryTextColor, fontWeight: 500 }}>
                              {apt.service}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.8 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, color: secondaryTextColor }}>
                            <AccessTimeRoundedIcon sx={{ fontSize: "0.85rem", color: tealButton }} />
                            <Typography sx={{ fontSize: "0.74rem", fontWeight: 600 }}>
                              {apt.time}
                            </Typography>
                          </Box>

                          <Chip
                            icon={
                              apt.type === "Telehealth" ? (
                                <VideoCallRoundedIcon sx={{ fontSize: "0.95rem !important", color: `${tealButton} !important` }} />
                              ) : (
                                <PersonRoundedIcon sx={{ fontSize: "0.95rem !important", color: `${tealButton} !important` }} />
                              )
                            }
                            label={apt.type}
                            size="small"
                            sx={{
                              height: "22px",
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              backgroundColor: "#FFFFFF",
                              color: tealButton,
                              border: "1px solid rgba(36, 94, 101, 0.25)",
                              borderRadius: "6px",
                            }}
                          />

                          <Chip
                            label={apt.status}
                            size="small"
                            sx={{
                              height: "22px",
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              backgroundColor: tealButton,
                              color: "#FFFFFF",
                              borderRadius: "6px",
                            }}
                          />

                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <Tooltip title="Call Patient">
                              <IconButton size="small" sx={{ color: secondaryTextColor, "&:hover": { color: tealMain } }}>
                                <PhoneInTalkOutlinedIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Chart Encounter">
                              <IconButton size="small" sx={{ color: secondaryTextColor, "&:hover": { color: tealMain } }}>
                                <MedicalInformationOutlinedIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>
                            <IconButton size="small" sx={{ color: secondaryTextColor, "&:hover": { color: tealMain } }}>
                              <MoreVertRoundedIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Scheduler;